import React, { useEffect, useState } from 'react';
import { PhoneIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

/**
 * Live preview beside every step of the Qualifying Agent flow: what the call says or the
 * WhatsApp leg sends, in order. Shows the selected channel; for Call + WhatsApp it opens on the
 * call, with a toggle.
 *
 * Props:
 *  @param {object}   channel     { mode: 'call'|'whatsapp'|'both' }
 *  @param {object}   details     { orgName, role, showRole }
 *  @param {Array}    questions   [{ id, text, kind }]
 */
export default function QualifyingAgentPreviewPanel({ channel, details, questions }) {
  // Single-channel modes force the view; "both" opens on the call, and the toggle pick is
  // dropped whenever the channel changes.
  const [picked, setPicked] = useState(null);
  useEffect(() => setPicked(null), [channel.mode]);
  const showToggle = channel.mode === 'both';
  const view = channel.mode === 'call' || channel.mode === 'whatsapp' ? channel.mode : picked || 'call';
  const setView = setPicked;

  const orgName = details.orgName?.trim() || 'Your Company';
  // Same placeholder rules as the server (qualifying-agent.service.js resolvePlaceholders).
  const roleName = details.showRole === false ? '' : details.role?.trim() || '';
  const resolve = (text) => {
    let s = String(text || '');
    if (!roleName) {
      s = s
        .replace(/\ban opening for the \{\{role\}\} (job role|role|position|job)\b/gi, 'a job opening')
        .replace(/\bthe \{\{role\}\} (job role|role|position|job|opening)\b/gi, 'a job opening');
    }
    return s.replace(/\{\{role\}\}/gi, roleName || 'this role').replace(/\{\{company\}\}/gi, orgName);
  };
  const shown = questions.filter((q) => q.text.trim()).map((q) => ({ ...q, text: resolve(q.text) }));

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ── Header row ───────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-2.5 shrink-0">
        <div className="flex items-center gap-1.5">
          {view === 'whatsapp'
            ? <ChatBubbleLeftRightIcon className="h-3.5 w-3.5 text-[#25D366]" />
            : <PhoneIcon className="h-3.5 w-3.5 text-[#007BFF]" />}
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
            {view === 'whatsapp' ? 'WhatsApp Preview' : 'Call Script Preview'}
          </p>
        </div>

        {/* Toggle — only when Both channels */}
        {showToggle && (
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <ToggleBtn active={view === 'whatsapp'} color="#25D366" onClick={() => setView('whatsapp')}>
              <ChatBubbleLeftRightIcon className="h-3 w-3" /> WA
            </ToggleBtn>
            <ToggleBtn active={view === 'call'} color="#007BFF" onClick={() => setView('call')}>
              <PhoneIcon className="h-3 w-3" /> Call
            </ToggleBtn>
          </div>
        )}
      </div>

      {/* ── Preview frame ────────────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
        {view === 'whatsapp'
          ? <WAPreview orgName={orgName} questions={shown} />
          : <CallPreview orgName={orgName} questions={shown} />}
      </div>

      <p className="mt-1.5 text-[9px] text-gray-400 text-center shrink-0 leading-tight">
        Preview only — exact wording adapts to candidate responses at runtime.
      </p>
    </div>
  );
}

// ── WhatsApp Preview ─────────────────────────────────────────────────────────

function WAPreview({ orgName, questions }) {
  // What the WhatsApp leg sends (server qualifyingWhatsApp.service.js): the opening template,
  // then each question as "Question i of n".
  const thread = [
    {
      from: 'agent',
      text: 'Hi Priya, this is regarding a role you applied for. We’d like to ask a few quick questions over WhatsApp — it’ll take about 2 minutes. Reply YES to get started, or STOP if you’d rather not.',
    },
    { from: 'candidate', text: 'YES' },
  ];
  // As the server sends them (qualifyingWhatsApp.service.js questionMessage): the intro is answered
  // by the YES above, never numbered; progress follows each question in italics (22 Oct).
  const asked = questions.filter((q) => q.kind !== 'intro');
  asked.forEach((q, i) => {
    thread.push({ from: 'agent', text: `${q.text}\n\n_${i + 1} of ${asked.length}_` });
    thread.push({ from: 'candidate', text: '…', placeholder: true });
  });

  // 3. Closing
  thread.push({
    from: 'agent',
    text: `Thank you for your time! \uD83D\uDE4F Our team at *${orgName}* will review your responses and reach out soon.\n\nHave a great day! \u2728`,
  });

  return (
    <div className="flex flex-col h-full" style={{ background: '#E5DDD5' }}>
      {/* WhatsApp header */}
      <div
        className="flex items-center gap-2.5 px-3 py-2.5 shrink-0"
        style={{ background: '#075E54' }}
      >
        {/* Avatar */}
        <div
          className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
          style={{ background: '#128C7E' }}
        >
          {orgName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-[11px] font-semibold truncate">{orgName}</p>
          <p className="text-white/60 text-[9px]">Business Account · TalentCrux</p>
        </div>
        {/* Icons row */}
        <div className="flex items-center gap-2">
          <PhoneIcon className="h-3.5 w-3.5 text-white/60" />
          <svg className="h-3.5 w-3.5 text-white/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" />
          </svg>
        </div>
      </div>

      {/* Chat area */}
      <div
        className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5"
        style={{
          backgroundImage:
            `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23b7b7b7' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        {/* Date pill */}
        <div className="flex justify-center mb-1">
          <span
            className="text-[9px] font-medium px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(0,0,0,0.18)', color: 'rgba(255,255,255,0.85)' }}
          >
            Today
          </span>
        </div>

        {thread.map((msg, i) => {
          const isAgent = msg.from === 'agent';
          return (
            <div key={i} className={`flex ${isAgent ? 'justify-start' : 'justify-end'}`}>
              <div
                className="max-w-[82%] px-2.5 py-1.5 rounded-xl shadow-sm relative"
                style={{
                  background: isAgent ? '#FFFFFF' : '#DCF8C6',
                  borderRadius: isAgent ? '0 12px 12px 12px' : '12px 0 12px 12px',
                }}
              >
                {/* Message text — parse basic *bold* */}
                <p className="text-[10px] text-gray-800 leading-relaxed whitespace-pre-wrap">
                  {parseBold(msg.text)}
                </p>

                {/* Time + status */}
                <div className="flex items-center justify-end gap-0.5 mt-0.5">
                  <span className="text-[8px] text-gray-400">{timeOffset(i)}</span>
                  {!isAgent && (
                    <span className="text-[9px]" style={{ color: '#4FC3F7' }}>
                      {msg.placeholder ? '\u2713' : '\u2713\u2713'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input bar */}
      <div
        className="flex items-center gap-2 px-2 py-1.5 shrink-0"
        style={{ background: '#F0F0F0' }}
      >
        <div
          className="flex-1 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] text-gray-400"
          style={{ background: '#FFFFFF' }}
        >
          <svg className="h-3 w-3 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M12 20h9M16.5 3.5l4 4L7 21H3v-4L16.5 3.5z" />
          </svg>
          Type a message
        </div>
        <div
          className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
          style={{ background: '#075E54' }}
        >
          <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </div>
      </div>
    </div>
  );
}

// ── Call Script Preview ───────────────────────────────────────────────────────

// The intro as the call says it (server opening.js buildOpening): the recruiter's words, with
// "Are you interested in applying?" added when they do not end by asking for a go-ahead.
const GO_AHEAD = /\b(ready|begin|start|good time|right time|okay|ok|shall we|can we|continue|proceed|talk|a few minutes|a minute|free now|interested)\b/i;
function introCallLine(text) {
  const words = String(text || '').replace(/^\s*(hi|hello|hey|namaste)( there)?\s*([!.,]\s*|$)/i, '').trim();
  const last = (words.match(/[^.!?]+[.!?]*/g) || []).pop()?.trim() || '';
  if (last.endsWith('?') && GO_AHEAD.test(last)) return words;
  return `${words}${/[.!?]$/.test(words) || !words ? '' : '.'} Are you interested in applying?`.trim();
}

function CallPreview({ orgName, questions }) {
  // What the call says (server opening.js): "Hi {name}!" + the intro, a short thank-you after the
  // yes, then the role brief and each question in order.
  const [first, ...rest] = questions;
  const hasIntro = first?.kind === 'intro';
  const opening = hasIntro
    ? [
        { who: 'agent', text: `Hi Priya! ${introCallLine(first.text)}` },
        { who: 'candidate', text: 'Yes, go ahead.' },
        { who: 'agent', text: 'Great, thank you!' },
      ]
    : [{ who: 'agent', text: `Hi Priya! We're calling from ${orgName} about your job application.` }];

  const lines = [
    ...opening,
    ...(hasIntro ? rest : questions).flatMap((q) => [
      { who: 'agent', text: q.text },
      { who: 'candidate', text: 'Candidate\u2019s answer\u2026' },
    ]),
    { who: 'agent', text: `Thank you! Our team at ${orgName} will be in touch soon. Have a great day!` },
  ];

  return (
    <div className="flex flex-col h-full" style={{ background: 'linear-gradient(160deg,#1a1a2e 0%,#16213e 100%)' }}>
      {/* Status */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1 shrink-0">
        <span className="text-white/40 text-[9px]">9:41 AM</span>
        <span className="text-white/40 text-[9px]">LTE</span>
      </div>

      {/* Call card */}
      <div className="flex flex-col items-center pt-3 pb-2 px-4 shrink-0">
        <div className="h-12 w-12 rounded-full flex items-center justify-center mb-2" style={{ background: 'rgba(0,123,255,0.18)', border: '2px solid rgba(0,123,255,0.3)' }}>
          <PhoneIcon className="h-5 w-5 text-[#60a5fa]" />
        </div>
        <p className="text-white text-xs font-bold">{orgName}</p>
        <p className="text-white/40 text-[9px] mt-0.5">TalentCrux Qualifying Agent</p>
        <div className="flex items-center gap-1.5 mt-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[#4ade80] animate-pulse" />
          <span className="text-[#4ade80] text-[9px] font-semibold">Call in progress</span>
        </div>
      </div>

      {/* Script */}
      <div className="flex-1 overflow-y-auto px-3 space-y-1.5 pb-3">
        {lines.map((l, i) => (
          <div
            key={i}
            className="rounded-lg px-2.5 py-2 text-[10px] leading-relaxed"
            style={
              l.who === 'agent'
                ? { background: 'rgba(0,123,255,0.12)', border: '1px solid rgba(0,123,255,0.2)', color: '#93c5fd' }
                : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }
            }
          >
            <span className="block text-[8px] font-bold uppercase tracking-wider mb-0.5" style={{ color: l.who === 'agent' ? '#60a5fa' : 'rgba(255,255,255,0.25)' }}>
              {l.who === 'agent' ? '\uD83E\uDD16 Agent' : '\uD83D\uDC64 Candidate'}
            </span>
            {l.text}
          </div>
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-around py-2.5 px-4 border-t border-white/10 shrink-0">
        {[{ e: '\uD83D\uDD07', l: 'Mute' }, { e: '\uD83D\uDCDE', l: 'End', red: true }, { e: '\uD83D\uDD0A', l: 'Speaker' }].map(({ e, l, red }) => (
          <div key={l} className="flex flex-col items-center gap-1">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm ${red ? 'bg-red-500' : 'bg-white/10'}`}>{e}</div>
            <span className="text-white/30 text-[8px]">{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function ToggleBtn({ active, color, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold transition-all',
        active ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700',
      ].join(' ')}
      style={active ? { color } : {}}
    >
      {children}
    </button>
  );
}

/** Render *bold* text as <strong> spans */
/** WhatsApp's *bold* and _italic_. */
function parseBold(text) {
  const parts = text.split(/(\*[^*]+\*|_[^_\n]+_)/g);
  return parts.map((p, i) =>
    p.length > 2 && p.startsWith('*') && p.endsWith('*') ? (
      <strong key={i} className="font-semibold">{p.slice(1, -1)}</strong>
    ) : p.length > 2 && p.startsWith('_') && p.endsWith('_') ? (
      <em key={i} className="text-gray-500">{p.slice(1, -1)}</em>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

function timeOffset(idx) {
  const d = new Date();
  d.setMinutes(d.getMinutes() - Math.max(0, 15 - idx));
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
